import React, { useMemo } from "react";
import { Modal, Table, Tag, Empty } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export type LogEntry = {
  raw_time?: string | null;
  check_time?: string | null;
  ip_address?: string | null;
};

export interface LogsModalProps {
  open: boolean;
  onClose: () => void;
  employee?: string;
  logs?: LogEntry[];
}

interface SessionRow {
  key: number;
  index: number;
  checkIn: string;
  checkOut: string;
  duration: string;
  hasCheckOut: boolean;
}

const fmtTime = (t?: string | null): string => {
  if (!t) return "—";
  const m = dayjs(t, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
  return m.isValid() ? m.format("hh:mm A") : t;
};

const fmtDuration = (startISO?: string | null, endISO?: string | null): string => {
  if (!startISO || !endISO) return "—";
  const start = dayjs(startISO);
  const end = dayjs(endISO);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return "—";
  const mins = end.diff(start, "minute");
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};

const pairLogs = (logs: LogEntry[] = []): SessionRow[] => {
  const sorted = [...logs].sort(
    (a, b) => dayjs(a.raw_time || 0).valueOf() - dayjs(b.raw_time || 0).valueOf(),
  );
  const pairs: SessionRow[] = [];
  for (let i = 0; i < sorted.length; i += 2) {
    const inLog = sorted[i];
    const outLog = sorted[i + 1];
    pairs.push({
      key: i,
      index: pairs.length + 1,
      checkIn: fmtTime(inLog?.check_time || undefined),
      checkOut: outLog?.check_time ? fmtTime(outLog.check_time) : "N/A",
      duration: outLog?.raw_time
        ? fmtDuration(inLog?.raw_time, outLog.raw_time)
        : "—",
      hasCheckOut: Boolean(outLog?.check_time),
    });
  }
  return pairs;
};

const LogsModal: React.FC<LogsModalProps> = ({ open, onClose, employee, logs }) => {
  const pairs = useMemo(() => pairLogs(logs || []), [logs]);

  const columns: ColumnsType<SessionRow> = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 60,
      align: "center",
      render: (value: number) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 28,
            height: 24,
            padding: "0 8px",
            background: "#f1f5f9",
            color: "#475569",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 4,
          }}
        >
          {value}
        </span>
      ),
    },
    {
      title: (
        <span>
          <i className="ti ti-login me-1" />
          Check-In
        </span>
      ),
      dataIndex: "checkIn",
      key: "checkIn",
      render: (value: string) => (
        <Tag
          color="green"
          style={{ fontSize: 12, fontWeight: 500, margin: 0, padding: "2px 10px" }}
        >
          <i className="ti ti-login me-1" />
          {value}
        </Tag>
      ),
    },
    {
      title: (
        <span>
          <i className="ti ti-logout me-1" />
          Check-Out
        </span>
      ),
      dataIndex: "checkOut",
      key: "checkOut",
      render: (value: string, record) => {
        if (!record.hasCheckOut) {
          return (
            <Tag color="red" style={{ fontSize: 12, margin: 0, padding: "2px 10px" }}>
              <i className="ti ti-alert-circle me-1" />
              Not recorded
            </Tag>
          );
        }
        return (
          <Tag
            color="blue"
            style={{ fontSize: 12, fontWeight: 500, margin: 0, padding: "2px 10px" }}
          >
            <i className="ti ti-logout me-1" />
            {value}
          </Tag>
        );
      },
    },
    {
      title: (
        <span>
          <i className="ti ti-hourglass-high me-1" />
          Duration
        </span>
      ),
      dataIndex: "duration",
      key: "duration",
      width: 120,
      render: (value: string) => (
        <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>{value}</span>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="d-flex align-items-center">
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "#eff6ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 12,
            }}
          >
            <i className="ti ti-clipboard-list" style={{ color: "#1d71e3", fontSize: 18 }} />
          </div>
          <div className="d-flex flex-column">
            <span style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>
              Logs{employee ? ` — ${employee}` : ""}
            </span>
            <span style={{ fontSize: 12, color: "#64748b" }}>
              <i className="ti ti-clock-hour-4 me-1" />
              {pairs.length
                ? `${pairs.length} session${pairs.length > 1 ? "s" : ""}`
                : "No sessions"}
            </span>
          </div>
        </div>
      }
      width={680}
      zIndex={1001}
      destroyOnClose
      footer={
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-3" style={{ fontSize: 12 }}>
            <span className="d-inline-flex align-items-center" style={{ color: "#475569" }}>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#10b981",
                  marginRight: 6,
                }}
              />
              Check-In
            </span>
            <span className="d-inline-flex align-items-center" style={{ color: "#475569" }}>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#3b82f6",
                  marginRight: 6,
                }}
              />
              Check-Out
            </span>
          </div>
          <button type="button" className="btn btn-primary px-4" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {pairs.length > 0 ? (
        <Table
          columns={columns}
          dataSource={pairs}
          rowKey="key"
          size="middle"
          pagination={false}
          scroll={{ y: 360 }}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: "#94a3b8" }}>No log entries available</span>
          }
          style={{ padding: "32px 0" }}
        />
      )}
    </Modal>
  );
};

export default LogsModal;
